// src/utils/audio.ts

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return null;
    if (!ctx || ctx.state === "closed") ctx = new AudioCtx();
    return ctx;
  } catch { return null; }
}

function isSamsungBrowser(): boolean {
  return /SamsungBrowser/i.test(navigator.userAgent);
}

function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent) && !/iPhone|iPad|iPod/i.test(navigator.userAgent);
}


// ── Hlasy ────────────────────────────────────────────────────
let _voices: SpeechSynthesisVoice[] = [];

function loadVoices() {
  if (!window.speechSynthesis) return;
  const v = window.speechSynthesis.getVoices();
  if (v.length > 0) { _voices = v; }
}

if (typeof window !== "undefined" && window.speechSynthesis) {
  // Na Android Chrome se hlasy načtou až po voiceschanged
  window.speechSynthesis.addEventListener("voiceschanged", () => {
    loadVoices();
  });
  loadVoices();
}

function pickVoice(lang: string): SpeechSynthesisVoice | undefined {
  if (_voices.length === 0) { loadVoices(); }
  const prefix = lang.split("-")[0];
  return (
    _voices.find(v => v.lang === lang) ||
    _voices.find(v => v.lang.startsWith(prefix))
  );
}

// Na Androidu musí být první speak() voláno přímo z gesta uživatele
// (unlockAudio z onTouchStart).

// ── unlockAudio ───────────────────────────────────────────────
// Volá se z onTouchStart na tlačítkách – zajistí AudioContext + TTS unlock
export function unlockAudio(): void {
  // 1. AudioContext unlock (pro beep)
  const c = getCtx();
  if (c?.state === "suspended") c.resume().catch(() => {});
  try {
    if (c) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      gain.gain.value = 0.00001;
      osc.connect(gain); gain.connect(c.destination);
      osc.start(c.currentTime); osc.stop(c.currentTime + 0.01);
    }
  } catch {}

  // 2. TTS unlock – jen iOS Safari (ne Android – spotřebovalo by user gesture)
  // Na Android unlock provádí první reálné speak() volání
  if (!isSamsungBrowser() && !isAndroid()) {
    try {
      if (window.speechSynthesis) {
        const utt = new SpeechSynthesisUtterance(" ");
        utt.volume = 0;
        utt.lang = "cs-CZ";
        window.speechSynthesis.speak(utt);
      }
    } catch {}
  }

  // 3. Na Android: načíst hlasy pokud ještě nenačteny
  if (isAndroid()) loadVoices();
}

// ── Fronta pro Android ────────────────────────────────────────
// Android Chrome neumí cancel() + okamžité speak() – použijeme frontu
let _androidQueue: string[] = [];
let _androidSpeaking = false;

function androidSpeak(text: string, lang: string, rate: number): void {
  const synth = window.speechSynthesis;
  if (!synth) return;

  // Pokud právě mluví, přepíšeme frontu jen s novým textem (staré přeskočit)
  if (_androidSpeaking) {
    _androidQueue = [text]; // jen poslední text
    return;
  }

  _androidSpeaking = true;
  _androidQueue = [];

  const utt = new SpeechSynthesisUtterance(text);
  utt.lang = lang;
  utt.rate = rate;
  utt.volume = 1;

  const voice = pickVoice(lang);
  if (voice) { utt.voice = voice; utt.lang = voice.lang; }

  // Keepalive pro Android (Chrome zastavuje TTS po 15s)
  let ka: ReturnType<typeof setInterval> | null = null;
  utt.onstart = () => {
    ka = setInterval(() => {
      if (!synth.speaking) { if (ka) clearInterval(ka); return; }
      synth.pause(); synth.resume();
    }, 5000);
  };

  const done = () => {
    if (ka) clearInterval(ka);
    _androidSpeaking = false;
    // Zpracuj další z fronty
    if (_androidQueue.length > 0) {
      const next = _androidQueue.shift()!;
      setTimeout(() => androidSpeak(next, lang, rate), 50);
    }
  };
  utt.onend = done;
  utt.onerror = done;

  // NA ANDROID: NEVOLAT cancel() před speak() – rozbíjí user gesture chain
  synth.speak(utt);
}

// ── speak ─────────────────────────────────────────────────────
// speakQueued: rychlé číslo do fronty BEZ cancel – pro 5,4,3,2,1 odpočet
// Cancel by přerušil předchozí číslo! Čísla musí jít za sebou.
export function speakQueued(text: string, lang = "cs-CZ", rate = 3.0): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (isAndroid()) { androidSpeak(text, lang, rate); return; }
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = lang; utt.rate = rate; utt.volume = 1;
    const voice = pickVoice(lang);
    if (voice) { utt.voice = voice; utt.lang = voice.lang; }
    synth.speak(utt); // přidá do fronty, nepřerušuje
  } catch {}
}

// speakCancelFirst: cancel + okamžité speak – pro fráze kde chceme přerušit
export function speakCancelFirst(text: string, lang = "cs-CZ", rate = 1.1): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (isAndroid()) { androidSpeak(text, lang, rate); return; }
    synth.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = lang; utt.rate = rate; utt.volume = 1;
    const voice = pickVoice(lang);
    if (voice) { utt.voice = voice; utt.lang = voice.lang; }
    synth.speak(utt);
  } catch {}
}

export function speak(text: string, lang = "cs-CZ", rate = 0.82): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;

    if (isAndroid()) {
      androidSpeak(text, lang, rate);
      return;
    }

    // iOS / Desktop / Samsung
    const samsung = isSamsungBrowser();
    if (!samsung) synth.cancel();

    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = lang;
    utt.rate = rate;
    utt.volume = 1;

    const voice = pickVoice(lang);
    if (voice) { utt.voice = voice; utt.lang = voice.lang; }
    else if (samsung) { utt.lang = ""; }

    let ka: ReturnType<typeof setInterval> | null = null;
    utt.onstart = () => {
      ka = setInterval(() => {
        if (!synth.speaking) { if (ka) clearInterval(ka); return; }
        synth.pause(); synth.resume();
      }, 5000);
    };
    const done = () => { if (ka) clearInterval(ka); };
    utt.onend = done;
    utt.onerror = done;

    synth.speak(utt);
  } catch {}
}

// ── playBeep ──────────────────────────────────────────────────
export function playBeep(freq: number, vol: number, duration: number): void {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") {
    c.resume().then(() => scheduleBeep(c, freq, vol, duration)).catch(() => {});
    return;
  }
  scheduleBeep(c, freq, vol, duration);
}

function scheduleBeep(c: AudioContext, freq: number, vol: number, duration: number): void {
  try {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain); gain.connect(c.destination);
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
    osc.start(c.currentTime); osc.stop(c.currentTime + duration);
  } catch {}
}

// ── timerAnnounce ─────────────────────────────────────────────
// iOS/PC: hlasové odpočítávání
// Android: beepy
//
// Fáze 3 (G2): naplánované hlášky iOS/PC jdou zrušit
// (cancelTimerAnnouncements – pauza, reset, další střelec, odchod), jinak
// po pauze nebo u dalšího střelce dál zaznělo „tři… konec“. Zrušení nikdy
// nevolá speechSynthesis.cancel() – na Androidu rozbíjí řetěz gest a jinde
// by usekl právě vyslovené „Zásah“ (docs/live-faze3-navrh.md bod 5.5).
// Časomíra hlásí při překročení prahu (isTimerThreshold) a předá skutečný
// zbývající čas – po dohnání (skrytá stránka, vytížený tablet) se hlášky
// naplánují podle něj a ty, které už minuly, se vynechají.

/** Naplánované hlášky odpočtu (iOS/PC) – zruší je cancelTimerAnnouncements. */
let announceTimers: Array<ReturnType<typeof setTimeout>> = [];

/** Hláška v čase „zbývá atMs“ (při remainingMs zbývajících); prošlá se vynechá. */
function scheduleAt(remainingMs: number, atMs: number, text: string, keepLate = false): void {
  let delay = remainingMs - atMs;
  if (delay < 0) {
    // Mírně opožděná hláška (do 0,3 s) ještě zazní hned; „konec“ vždy
    if (!keepLate && delay < -300) return;
    delay = 0;
  }
  const t = setTimeout(() => {
    announceTimers = announceTimers.filter(x => x !== t);
    speakCancelFirst(text);
  }, delay);
  announceTimers.push(t);
}

/** Zruší naplánované hlášky odpočtu (nikdy speechSynthesis.cancel()). */
export function cancelTimerAnnouncements(): void {
  announceTimers.forEach(t => clearTimeout(t));
  announceTimers = [];
}

/** Má časomíra při této zbývající sekundě něco ohlásit (podle platformy)? */
export function isTimerThreshold(secsRemaining: number, timeLimit: number): boolean {
  if (isAndroid()) {
    if (secsRemaining === 0) return true;
    if (secsRemaining === 60 && timeLimit > 65) return true;
    if (secsRemaining === 30 && timeLimit > 35) return true;
    if (secsRemaining === 10 && timeLimit > 12) return true;
    return secsRemaining >= 1 && secsRemaining <= 5;
  }
  if (secsRemaining === 6 && timeLimit > 6) return true;
  if (secsRemaining === 31 && timeLimit > 35) return true;
  if (secsRemaining === 11 && timeLimit > 14) return true;
  return secsRemaining === 62 && timeLimit > 65;
}

export function timerAnnounce(
  secsRemaining: number,
  timeLimit: number,
  remainingMs: number = secsRemaining * 1000,
  options: { resumed?: boolean } = {}
): void {
  if (isAndroid()) {
    if (secsRemaining === 0) {
      // Konec – dlouhý píp
      playBeep(880, 1.0, 1.5);
      return;
    }
    if (secsRemaining === 60 && timeLimit > 65) {
      // Minuta – 1× krátký píp
      playBeep(660, 0.7, 0.15);
      return;
    }
    if (secsRemaining === 30 && timeLimit > 35) {
      // 30s – 1× krátký píp
      playBeep(660, 0.7, 0.15);
      return;
    }
    if (secsRemaining === 10 && timeLimit > 12) {
      // 10s – 1× krátký píp
      playBeep(660, 0.7, 0.15);
      return;
    }
    if (secsRemaining >= 1 && secsRemaining <= 5) {
      // 5,4,3,2,1 – tón se zvyšuje
      const freq = 600 + (6 - secsRemaining) * 60; // 660→900 Hz
      playBeep(freq, 0.7, 0.15);
      return;
    }
    return;
  }

  // iOS / Desktop – hlasový odpočet
  //
  // Strategie: při secsRemaining=6 se naplánuje celý odpočet – každé číslo
  // půl sekundy před přechodem displeje (pět při 5,5 s … jedna při 1,5 s),
  // „konec“ přesně při 0. Rate=3.0 → každé slovo ~220ms.
  //
  if (secsRemaining === 6 && timeLimit > 6) {
    // Při pokračování po pauze bez cancel() – neusekne právě vyslovené „Zásah“
    if (!options.resumed && window.speechSynthesis) window.speechSynthesis.cancel();
    cancelTimerAnnouncements();
    scheduleAt(remainingMs, 5500, "pět");
    scheduleAt(remainingMs, 4500, "čtyři");
    scheduleAt(remainingMs, 3500, "tři");
    scheduleAt(remainingMs, 2500, "dva");
    scheduleAt(remainingMs, 1500, "jedna");
    scheduleAt(remainingMs, 0, "konec", true);
    return;
  }
  // Ignoruj 5-0 – countdown naplánován
  if (secsRemaining >= 0 && secsRemaining <= 5) { return; }
  //
  // Delší fráze s 0.5s předstihem
  if (secsRemaining === 31 && timeLimit > 35) {
    scheduleAt(remainingMs, 30500, "zbývá třicet sekund"); return;
  }
  if (secsRemaining === 11 && timeLimit > 14) {
    scheduleAt(remainingMs, 10500, "zbývá deset sekund"); return;
  }
  if (secsRemaining === 62 && timeLimit > 65) {
    scheduleAt(remainingMs, 61500, "minuta do konce"); return;
  }
}
